import { useEffect, useRef } from "react";
import { PAYNOTE_PLAID_SUCCESS_QUERY_PARAM, PAYNOTE_PLAID_CANCEL_QUERY_PARAM } from "payment-provider/paynote/paynote.ts";
import useQueryParams from "utils/useQueryParams.ts";
import { useLocation, useNavigate } from "react-router-dom";
import { useAppDispatch } from "state/hooks.ts";
import { fetchBankAccounts, setPolling } from "state/slices/bank-accounts-slice.ts";
import type { BankAccount, BankAccountsResponse } from "interfaces/commerce/bank-account.ts";

interface Params {
  whenRedirectedFromPlaidSuccess?: () => void;
  whenRedirectedFromPlaidCancel?: () => void;
  removePaynoteParameters?: boolean;
}

const POLLING_INTERVAL = 3000;
const MAX_POLLING_DURATION = 3 * 60 * 1000;
const POLLING_STORAGE_KEY = "paynote_plaid_polling_started_at";

const hasRecentFundingSource = ({ accounts }: BankAccountsResponse): boolean => {
  const threeMinutesAgo = new Date(Date.now() - MAX_POLLING_DURATION);

  return accounts.some((source: BankAccount) => {
    const createdAt = new Date(source.created_at);
    return createdAt > threeMinutesAgo;
  });
};

const setPollingFlag = () => {
  localStorage.setItem(POLLING_STORAGE_KEY, Date.now().toString());
};

const getPollingFlag = (): number | null => {
  const timestamp = localStorage.getItem(POLLING_STORAGE_KEY);
  return timestamp ? parseInt(timestamp, 10) : null;
};

const clearPollingFlag = () => {
  localStorage.removeItem(POLLING_STORAGE_KEY);
};

const shouldPoll = (): boolean => {
  const timestamp = getPollingFlag();
  if (!timestamp) return false;
  
  const elapsedTime = Date.now() - timestamp;
  return elapsedTime < MAX_POLLING_DURATION;
};

export const usePaynotePlaidRedirectHandler = ({
  whenRedirectedFromPlaidSuccess = () => {},
  whenRedirectedFromPlaidCancel = () => {},
  removePaynoteParameters = true
}: Params) => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const queryParams = useQueryParams();
  
  const pollingIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const pollingStartTimeRef = useRef<number | null>(null);

  const plaidSuccess = queryParams.get(PAYNOTE_PLAID_SUCCESS_QUERY_PARAM);
  const plaidCancel = queryParams.get(PAYNOTE_PLAID_CANCEL_QUERY_PARAM);

  const stopPolling = () => {
    dispatch(setPolling(false));
    pollingStartTimeRef.current = null;
    clearPollingFlag();
    
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
  };

  const startPolling = () => {
    const startTime = getPollingFlag() || Date.now();
    pollingStartTimeRef.current = startTime;
    dispatch(setPolling(true));
    
    if (!getPollingFlag()) {
      setPollingFlag();
    }

    const checkFundingSources = async () => {
      if (pollingStartTimeRef.current && Date.now() - pollingStartTimeRef.current >= MAX_POLLING_DURATION) {
        stopPolling();
        return;
      }

      try {
        const result = await dispatch(fetchBankAccounts()).unwrap();
        
        if (hasRecentFundingSource(result)) {
          stopPolling();
        }
      } catch (error) {
        console.error("❌ Polling error:", error);
        stopPolling();
      }
    };

    checkFundingSources();
    pollingIntervalRef.current = setInterval(checkFundingSources, POLLING_INTERVAL);
  };

  useEffect(() => {
    if (shouldPoll()) {
      
      dispatch(fetchBankAccounts()).then((action) => {
        if (fetchBankAccounts.fulfilled.match(action)) {
          const fundingSources = action.payload;
          
          if (hasRecentFundingSource(fundingSources)) {
            stopPolling();
          } else {
            startPolling();
          }
        }
      });
    }
  }, []);

  useEffect(() => {
    if (plaidSuccess) {
      setPollingFlag();
      
      dispatch(fetchBankAccounts()).then((action) => {
        if (fetchBankAccounts.fulfilled.match(action)) {
          const fundingSources = action.payload;
          
          if (hasRecentFundingSource(fundingSources)) {
            stopPolling();
          } else {
            startPolling();
          }
          
          whenRedirectedFromPlaidSuccess();
        }
      });

      if (removePaynoteParameters) {
        // Remove only Paynote-specific parameters while preserving others
        const currentParams = new URLSearchParams(location.search);
        currentParams.delete(PAYNOTE_PLAID_SUCCESS_QUERY_PARAM);
        currentParams.delete(PAYNOTE_PLAID_CANCEL_QUERY_PARAM);
        
        const newUrl = currentParams.toString() 
          ? `${location.pathname}?${currentParams.toString()}`
          : location.pathname;
        
        navigate(newUrl, { replace: true });
      }
    } else if (plaidCancel) {
      clearPollingFlag();
      whenRedirectedFromPlaidCancel();
      if (removePaynoteParameters) {
        // Remove only Paynote-specific parameters while preserving others
        const currentParams = new URLSearchParams(location.search);
        currentParams.delete(PAYNOTE_PLAID_SUCCESS_QUERY_PARAM);
        currentParams.delete(PAYNOTE_PLAID_CANCEL_QUERY_PARAM);
        
        const newUrl = currentParams.toString() 
          ? `${location.pathname}?${currentParams.toString()}`
          : location.pathname;
        
        navigate(newUrl, { replace: true });
      }
    }

    return stopPolling;
  }, [plaidSuccess, plaidCancel]);

  useEffect(() => {
    return stopPolling;
  }, []);
}; 